import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, Bell, Clock, Mail, Merge, Star, StickyNote, Tag, Trash2, EyeOff, Eraser, UserRound } from "lucide-react";
import type { ContactRow } from "@hub/shared";
import { toast } from "@/ui";
import { openReach } from "./ReachLink";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { useCommandBarStore, useSelection, type CommandItem } from "@/app/navigation";
import { NoteEditor } from "./NoteEditor";
import { ReminderEditor } from "./ReminderEditor";
import { GroupPickerModal } from "./GroupPickerModal";
import { CadenceEditor } from "./CadenceEditor";
import { MergeModal } from "./MergeModal";
import { ConfirmModal } from "./ConfirmModal";

export type ContactAction = "note" | "reminder" | "group" | "cadence" | "merge" | "archive" | "delete" | "private" | "clear" | "star" | "contact";

/**
 * Owns the modals for the single-contact actions and (optionally) registers them in the command bar with their
 * single-key shortcuts. Mount one per screen for the contact the keyboard should act on.
 */
export function useContactActions(contactId: string | null, opts: { register?: boolean; row?: ContactRow | null; afterArchive?: () => void } = {}) {
  const { register = true, row: givenRow, afterArchive } = opts;
  const navigate = useNavigate();
  const [modal, setModal] = useState<ContactAction | null>(null);
  const rowQuery = useEngineQuery("getContactRows", { contactIds: contactId ? [contactId] : [] }, { enabled: !!contactId && !givenRow });
  const row = givenRow ?? rowQuery.data?.[0] ?? null;
  const star = useEngineMutation("setContactStarred");
  const archive = useEngineMutation("archiveContact");
  const del = useEngineMutation("deleteContact");
  const setPrivate = useEngineMutation("setContactPrivate");
  const setSelf = useEngineMutation("setSelfContact");
  const clear = useEngineMutation("clearContactProfile");
  const registerItems = useCommandBarStore((s) => s.register);
  const setFocused = useSelection((s) => s.setFocused);

  useEffect(() => {
    if (contactId) setFocused(contactId);
  }, [contactId, setFocused]);

  const items = useMemo<CommandItem[]>(() => {
    if (!contactId) return [];
    const name = row?.displayName ?? "this person";
    return [
      { id: "c.note", title: `Add a note about ${name}`, keywords: ["note", "write", "remember"], shortcut: "n", group: "Person", icon: StickyNote, run: () => setModal("note") },
      { id: "c.reminder", title: `Add a reminder about ${name}`, keywords: ["reminder", "remind", "follow up"], shortcut: "r", group: "Person", icon: Bell, run: () => setModal("reminder") },
      { id: "c.group", title: `Add ${name} to a group`, keywords: ["group", "label", "tag", "list"], shortcut: "l", group: "Person", icon: Tag, run: () => setModal("group") },
      { id: "c.cadence", title: `Edit reconnect cadence for ${name}`, keywords: ["cadence", "reconnect", "frequency", "keep in touch"], shortcut: "h", group: "Person", icon: Clock, run: () => setModal("cadence") },
      { id: "c.contact", title: `Contact ${name}`, keywords: ["email", "call", "text", "message", "contact", "reach"], shortcut: "c", group: "Person", icon: Mail, run: () => { if (row?.primaryPhone) openReach(`sms:${row.primaryPhone}`); else if (row?.primaryEmail) openReach(`mailto:${row.primaryEmail}`); else toast("No phone or email on file.", "danger"); } },
      { id: "c.star", title: row?.isStarred ? `Unstar ${name}` : `Star ${name}`, keywords: ["star", "favorite", "favourite", "unstar"], shortcut: "s", group: "Person", icon: Star, run: () => star.mutate({ contactId, starred: !row?.isStarred }, { onSuccess: () => toast(row?.isStarred ? "Removed from favorites" : "Added to favorites") }) },
      { id: "c.merge", title: `Merge ${name} with someone`, keywords: ["merge", "duplicate", "combine"], shortcut: "m", group: "Person", icon: Merge, run: () => setModal("merge") },
      { id: "c.archive", title: `Archive ${name}`, keywords: ["archive", "hide", "remove"], shortcut: "#", group: "Person", icon: Archive, run: () => setModal("archive") },
      { id: "c.me", title: `This is me (${name})`, keywords: ["me", "myself", "own card", "self"], group: "Person", icon: UserRound, run: () => { if (window.confirm(`Use ${name} as your own card? Your needs and offers will belong to this person.`)) setSelf.mutate({ contactId }, { onSuccess: () => toast(`${name} is now your own card.`, "success") }); } },
      { id: "c.private", title: row ? `Mark ${name} as private` : "Mark as private", keywords: ["private", "hide from matching"], group: "Person", icon: EyeOff, run: () => setModal("private") },
      { id: "c.clear", title: `Clear profile data for ${name}`, keywords: ["clear", "reset profile", "enrichment"], group: "Person", icon: Eraser, run: () => setModal("clear") },
      { id: "c.delete", title: `Delete ${name}`, keywords: ["delete", "remove permanently"], group: "Person", icon: Trash2, run: () => setModal("delete") },
    ];
  }, [contactId, row, star]);

  useEffect(() => {
    if (!register || items.length === 0) return;
    return registerItems(items);
  }, [register, items, registerItems]);

  const modals = contactId ? (
    <>
      <NoteEditor open={modal === "note"} onClose={() => setModal(null)} contactId={contactId} />
      <ReminderEditor open={modal === "reminder"} onClose={() => setModal(null)} contactId={contactId} />
      <GroupPickerModal open={modal === "group"} onClose={() => setModal(null)} contactIds={[contactId]} />
      <CadenceEditor open={modal === "cadence"} onClose={() => setModal(null)} contactId={contactId} />
      <MergeModal open={modal === "merge"} onClose={() => setModal(null)} contactIds={[contactId]} />
      <ConfirmModal
        open={modal === "archive"}
        title={`Archive ${row?.displayName ?? "this person"}?`}
        body="Archived people leave your lists and suggestions. You can restore them from Archive at any time."
        confirmLabel="Archive"
        danger
        loading={archive.isPending}
        onClose={() => setModal(null)}
        onConfirm={() => archive.mutate({ contactId }, { onSuccess: () => { setModal(null); toast("Archived"); afterArchive?.(); } })}
      />
      <ConfirmModal
        open={modal === "delete"}
        title={`Delete ${row?.displayName ?? "this person"}?`}
        body="This removes the person, their notes and reminders from every device. Archiving is reversible; deleting is not."
        confirmLabel="Delete"
        danger
        loading={del.isPending}
        onClose={() => setModal(null)}
        onConfirm={() => del.mutate({ contactId, confirm: true }, { onSuccess: () => { setModal(null); toast("Deleted"); navigate("/people"); } })}
      />
      <ConfirmModal
        open={modal === "private"}
        title="Mark as private?"
        body="Private people are excluded from matching and never shared with anyone. You can undo this from the person's menu."
        confirmLabel="Mark private"
        loading={setPrivate.isPending}
        onClose={() => setModal(null)}
        onConfirm={() => setPrivate.mutate({ contactId, isPrivate: true }, { onSuccess: () => { setModal(null); toast("Marked private"); } })}
      />
      <ConfirmModal
        open={modal === "clear"}
        title="Clear profile data?"
        body="Removes imported profile details (work history, bio, socials, interests). Names, sources, notes and interactions stay."
        confirmLabel="Clear profile"
        danger
        loading={clear.isPending}
        onClose={() => setModal(null)}
        onConfirm={() => clear.mutate({ contactId }, { onSuccess: () => { setModal(null); toast("Profile cleared"); } })}
      />
    </>
  ) : null;

  return { items, modals, open: (a: ContactAction) => setModal(a), row };
}
